import { Box, Typography, TextField } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import * as React from 'react';
import { useState } from 'react';
import { Vehicle, VEHICLE_FIELDS } from 'types/vehicles/types';
import { vehiclePageStyles } from '../styles';
import dayjs, { Dayjs } from 'dayjs';
import '../styles.css';

export interface VehicleFinancialsSectionData {
  invoice_date: string | null;
  sale_amount: number | string;
  fees_amount: number | string;
  gst_amount: number | string;
  pst_amount: number | string;
  total_amount: number | string;
}

interface VehicleFinancialsSectionProps {
  updateSubmitData: (data: VehicleFinancialsSectionData) => void;
  canEdit: (fieldName: string) => boolean;
}

export interface VehicleFinancialsSectionRef {
  updateSectionData: (vehicleData: Vehicle) => void;
}

const VehicleFinancialsSection = React.forwardRef(
  ({ updateSubmitData, canEdit }: VehicleFinancialsSectionProps, ref) => {
    // Financial states
    const [invoiceDateChoice, setInvoiceDateChoice] = useState<Dayjs | null>(null);
    const [saleAmountInput, setSaleAmountInput] = useState<number | string>('0.00');
    const [feesAmountInput, setFeesAmountInput] = useState<number | string>('0.00');
    const [gstAmountInput, setGstAmountInput] = useState<number | string>('0.00');
    const [pstAmountInput, setPstAmountInput] = useState<number | string>('0.00');
    const [totalAmountInput, setTotalAmountInput] = useState<number | string>('0.00');

    React.useEffect(() => {
      // When one of the internal states change, update the submit data
      updateSubmitData({
        // eslint-disable-next-line camelcase
        invoice_date: invoiceDateChoice ? dayjs(invoiceDateChoice).format('YYYY-MM-DD') : null,
        // eslint-disable-next-line camelcase
        sale_amount: saleAmountInput,
        // eslint-disable-next-line camelcase
        fees_amount: feesAmountInput,
        // eslint-disable-next-line camelcase
        gst_amount: gstAmountInput,
        // eslint-disable-next-line camelcase
        pst_amount: pstAmountInput,
        // eslint-disable-next-line camelcase
        total_amount: totalAmountInput,
      });
    }, [
      invoiceDateChoice,
      saleAmountInput,
      feesAmountInput,
      gstAmountInput,
      pstAmountInput,
      totalAmountInput,
      updateSubmitData,
    ]);

    React.useImperativeHandle(ref, () => ({
      updateSectionData: (vehicleData: Vehicle) => {
        setInvoiceDateChoice(vehicleData?.invoice_date ? dayjs(vehicleData.invoice_date) : null);
        setSaleAmountInput(vehicleData?.sale_amount || '0.00');
        setFeesAmountInput(vehicleData?.fees_amount || '0.00');
        setGstAmountInput(vehicleData?.gst_amount || '0.00');
        setPstAmountInput(vehicleData?.pst_amount || '0.00');
        setTotalAmountInput(vehicleData?.total_amount || '0.00');
      },
    }));

    return (
      <Box sx={vehiclePageStyles.card}>
        <Box sx={vehiclePageStyles.cardHeader}>Financials</Box>
        <Box sx={vehiclePageStyles.detailBox}>
          <Typography sx={vehiclePageStyles.detailTitle}>Invoice date</Typography>
          {canEdit(VEHICLE_FIELDS.INVOICE_DATE) ? (
            <DatePicker
              sx={{ ...vehiclePageStyles.detailTextField }}
              minDate={dayjs().subtract(1, 'year')}
              maxDate={dayjs().add(1, 'year')}
              value={invoiceDateChoice}
              onChange={newValue => setInvoiceDateChoice(newValue)}
              slotProps={{
                textField: {
                  size: 'small',
                  variant: 'standard',
                  InputProps: {
                    style: {
                      fontWeight: '500',
                    },
                  },
                },
              }}
            />
          ) : (
            <Box sx={vehiclePageStyles.detailValue}>
              {invoiceDateChoice ? dayjs(invoiceDateChoice).format('YYYY-MM-DD') : '-'}
            </Box>
          )}
        </Box>
        <Box sx={vehiclePageStyles.detailBox}>
          <Typography sx={vehiclePageStyles.detailTitle}>Sale amount</Typography>
          {canEdit(VEHICLE_FIELDS.SALE_AMOUNT) ? (
            <TextField
              variant="standard"
              size="small"
              id="sale-amount"
              value={saleAmountInput}
              type="number"
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                if (event.target.value) {
                  setSaleAmountInput(parseFloat(event.target.value));
                } else {
                  setSaleAmountInput('');
                }
              }}
              sx={vehiclePageStyles.detailTextField}
              inputProps={{
                min: 0,
                sx: {
                  ...vehiclePageStyles.detailTextFieldInput,
                },
              }}
            />
          ) : (
            <Box sx={vehiclePageStyles.detailValue}>{saleAmountInput || '-'}</Box>
          )}
        </Box>
        <Box sx={vehiclePageStyles.detailBox}>
          <Typography sx={vehiclePageStyles.detailTitle}>Fees</Typography>
          {canEdit(VEHICLE_FIELDS.FEES_AMOUNT) ? (
            <TextField
              variant="standard"
              size="small"
              id="fees-amount"
              value={feesAmountInput}
              type="number"
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                if (event.target.value) {
                  setFeesAmountInput(parseFloat(event.target.value));
                } else {
                  setFeesAmountInput('');
                }
              }}
              sx={vehiclePageStyles.detailTextField}
              inputProps={{
                min: 0,
                sx: {
                  ...vehiclePageStyles.detailTextFieldInput,
                },
              }}
            />
          ) : (
            <Box sx={vehiclePageStyles.detailValue}>{feesAmountInput || '-'}</Box>
          )}
        </Box>
        <Box sx={vehiclePageStyles.detailBox}>
          <Typography sx={vehiclePageStyles.detailTitle}>GST / HST</Typography>
          {canEdit(VEHICLE_FIELDS.GST_AMOUNT) ? (
            <TextField
              variant="standard"
              size="small"
              id="gst-amount"
              value={gstAmountInput}
              type="number"
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                if (event.target.value) {
                  setGstAmountInput(parseFloat(event.target.value));
                } else {
                  setGstAmountInput('');
                }
              }}
              sx={vehiclePageStyles.detailTextField}
              inputProps={{
                min: 0,
                sx: {
                  ...vehiclePageStyles.detailTextFieldInput,
                },
              }}
            />
          ) : (
            <Box sx={vehiclePageStyles.detailValue}>{gstAmountInput || '-'}</Box>
          )}
        </Box>
        <Box sx={vehiclePageStyles.detailBox}>
          <Typography sx={vehiclePageStyles.detailTitle}>PST / QST</Typography>
          {canEdit(VEHICLE_FIELDS.PST_AMOUNT) ? (
            <TextField
              variant="standard"
              size="small"
              id="pst-amount"
              value={pstAmountInput}
              type="number"
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                if (event.target.value) {
                  setPstAmountInput(parseFloat(event.target.value));
                } else {
                  setPstAmountInput('');
                }
              }}
              sx={vehiclePageStyles.detailTextField}
              inputProps={{
                min: 0,
                sx: {
                  ...vehiclePageStyles.detailTextFieldInput,
                },
              }}
            />
          ) : (
            <Box sx={vehiclePageStyles.detailValue}>{pstAmountInput || '-'}</Box>
          )}
        </Box>
        <Box sx={vehiclePageStyles.detailBox}>
          <Typography sx={vehiclePageStyles.detailTitle}>Total</Typography>
          {canEdit(VEHICLE_FIELDS.TOTAL_AMOUNT) ? (
            <TextField
              variant="standard"
              size="small"
              id="total-amount"
              value={totalAmountInput}
              type="number"
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                if (event.target.value) {
                  setTotalAmountInput(parseFloat(event.target.value));
                } else {
                  setTotalAmountInput('');
                }
              }}
              sx={vehiclePageStyles.detailTextField}
              inputProps={{
                min: 0,
                sx: {
                  ...vehiclePageStyles.detailTextFieldInput,
                },
              }}
            />
          ) : (
            <Box sx={vehiclePageStyles.detailValue}>{totalAmountInput || '-'}</Box>
          )}
        </Box>
      </Box>
    );
  }
);

VehicleFinancialsSection.displayName = 'VehicleFinancialsSection';

export default VehicleFinancialsSection;
